import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSuppliersWithGLNs, linkSuppliers, unlinkSupplier, SupplierGLNData } from '../../services/glnLinkingApi';
import { getSuppliers } from '../../services/api';
import { useToast } from '../ui/ToastProvider';
import DeleteConfirmModal from './DeleteConfirmModal';

// Icons
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const DragIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

const LinkIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

interface Supplier {
  _id: string;
  name: string;
  kreditorNumber?: string;
  email: string;
  isActive: boolean;
  isPrimary?: boolean;
  masterSupplierId?: string;
}

interface HierarchicalSupplier {
  _id: string;
  name: string;
  email: string;
  mainGLN?: string;
  kreditorNumber?: string;
  additionalGLNs: Array<{
    gln: string;
    description?: string;
    addedAt: string;
    addedBy?: string;
  }>;
  allGLNs: string[];
  articleCount: number;
  isPrimary?: boolean;
  isLinked?: boolean;
  isOrphanedLink?: boolean;
  masterSupplierId?: string;
  linkedSuppliers?: HierarchicalSupplier[];
  hasLinkedSuppliers?: boolean;
  totalArticles?: number;
  glnNumbers?: Array<{
    glnNumber: string;
    companyName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  }>;
}

interface DropZoneSupplier {
  _id: string;
  name: string;
  kreditorNumber?: string;
  isMainSupplier: boolean;
  linkedSuppliers: DropZoneSupplier[];
}

interface GLNLinkingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSupplier?: any;
  onSelectionComplete?: (supplier: any) => void;
  onLinkingComplete?: () => void;
  initialTab?: 'organize' | 'view';
  initialViewSearch?: string;
}

// Treffer in Name, Kreditor, E-Mail oder GLN
const matchesSupplier = (s: HierarchicalSupplier, term: string): boolean =>
  [s.name, s.kreditorNumber, s.email, ...(s.allGLNs || [])]
    .some(v => v && v.toLowerCase().includes(term));

export const GLNLinkingModal: React.FC<GLNLinkingModalProps> = ({
  isOpen,
  onClose,
  selectedSupplier,
  onSelectionComplete,
  onLinkingComplete,
  initialTab = 'organize',
  initialViewSearch = ''
}) => {
  // Toast notifications
  const { showSuccess, showError, showInfo } = useToast();
  
  // State management
  const [activeTab, setActiveTab] = useState<'organize' | 'view'>('organize');
  
  // Organize tab state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dropZoneSuppliers, setDropZoneSuppliers] = useState<DropZoneSupplier[]>([]);
  const [draggedSupplier, setDraggedSupplier] = useState<Supplier | null>(null);
  const [saving, setSaving] = useState(false);

  // View tab state  
  const [suppliers, setSuppliers] = useState<HierarchicalSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [viewSearch, setViewSearch] = useState('');
  const [onlyWithLinks, setOnlyWithLinks] = useState(true);

  const viewTerm = viewSearch.trim().toLowerCase();

  // Gruppen filtern: Treffer im Hauptkonto ODER in einem verknüpften Konto
  const visibleSuppliers = useMemo(() => suppliers.filter(s => {
    if (onlyWithLinks && !s.hasLinkedSuppliers && !s.isOrphanedLink) return false;
    if (!viewTerm) return true;
    return matchesSupplier(s, viewTerm) || (s.linkedSuppliers || []).some(l => matchesSupplier(l, viewTerm));
  }), [suppliers, onlyWithLinks, viewTerm]);

  const isGroupExpanded = (s: HierarchicalSupplier): boolean =>
    expandedSuppliers.has(s._id) ||
    (!!viewTerm && (s.linkedSuppliers || []).some(l => matchesSupplier(l, viewTerm)));

  // Delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    supplierId: string | null;
    supplierName: string;
  }>({
    isOpen: false,
    supplierId: null,
    supplierName: ''
  });

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset search state when modal opens
      setSearchTerm('');
      setSearchResults([]);
      setDropZoneSuppliers([]);
      setExpandedSuppliers(new Set());
      setViewSearch(initialViewSearch);

      if (initialTab !== activeTab) {
        // Laden übernimmt der Tab-Wechsel-Effekt
        setActiveTab(initialTab);
      } else if (initialTab === 'view') {
        loadSuppliersWithGLNs();
      } else {
        // Load initial 10 suppliers for organize tab
        console.log('🚀 Modal opened, loading initial suppliers...');
        loadInitialSuppliers();
      }
    }
  }, [isOpen]);

  // Handle tab changes
  useEffect(() => {
    if (isOpen && activeTab === 'view') {
      loadSuppliersWithGLNs();
    } else if (isOpen && activeTab === 'organize' && searchResults.length === 0 && !searchTerm && !searchLoading) {
      console.log('🔄 Tab changed to organize, loading suppliers...');
      loadInitialSuppliers();
    }
  }, [activeTab]);

  // Search debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'organize') {
        if (searchTerm.trim()) {
          searchSuppliers(searchTerm);
        } else {
          // Show initial 10 suppliers when no search term
          loadInitialSuppliers();
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, activeTab]); // Removed dropZoneSuppliers dependency

  const loadInitialSuppliers = async () => {
    console.log('🔄 Loading initial suppliers...');
    setSearchLoading(true);
    try {
      const response = await getSuppliers({
        page: 1,
        limit: 10,
        isActive: 'true'
      });
      
      console.log('📦 Initial suppliers response:', response);
      
      // Handle different response formats
      let suppliersData;
      if (response && response.data) {
        // If response has a data property
        if (Array.isArray(response.data)) {
          suppliersData = response.data;
        } else if (response.data.suppliers && Array.isArray(response.data.suppliers)) {
          suppliersData = response.data.suppliers;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          suppliersData = response.data.data;
        } else {
          suppliersData = [];
        }
      } else if (Array.isArray(response)) {
        // If response is directly an array
        suppliersData = response;
      } else {
        suppliersData = [];
      }
      
      const allSuppliers = Array.isArray(suppliersData) ? suppliersData : [];
      
      console.log('📋 All suppliers found:', allSuppliers.length);
      console.log('📋 Sample supplier:', allSuppliers[0]);
      
      // Get all already linked suppliers from existing hierarchical structure
      const linkedSupplierIds = new Set<string>();
      
      // Add suppliers from drop zone (both main suppliers and linked ones in drop zone)
      dropZoneSuppliers.forEach(group => {
        linkedSupplierIds.add(group._id); // Add main supplier from drop zone
        group.linkedSuppliers.forEach(linked => linkedSupplierIds.add(linked._id));
      });
      
      // Add suppliers that are already linked in the database (but NOT the main suppliers)
      try {
        const existingLinkedResponse = await getSuppliersWithGLNs();
        if (Array.isArray(existingLinkedResponse)) {
          existingLinkedResponse.forEach((supplier: HierarchicalSupplier) => {
            if (supplier.linkedSuppliers && supplier.linkedSuppliers.length > 0) {
              // DON'T add the main supplier (supplier._id) - only the linked ones
              supplier.linkedSuppliers.forEach(linked => {
                if (linked._id) linkedSupplierIds.add(linked._id);
              });
            }
          });
        }
      } catch (error) {
        console.warn('Warning: Could not load existing linked suppliers for filtering');
      }
      
      const filteredResults = allSuppliers.filter(
        (supplier: Supplier) => !linkedSupplierIds.has(supplier._id)
      );
      
      console.log('✅ Filtered suppliers to show:', filteredResults.length);
      console.log('🚫 Excluded linked suppliers:', linkedSupplierIds.size);
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('❌ Error loading initial suppliers:', error);
      showError('Fehler beim Laden der Lieferanten');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const unlinkSupplierFromGroup = async (linkedSupplierId: string) => {
    try {
      setLoading(true);
      
      await unlinkSupplier(linkedSupplierId);
      
      // Reload the suppliers with GLNs to reflect changes
      await loadSuppliersWithGLNs();
      showSuccess('Lieferant erfolgreich entverknüpft');
      onLinkingComplete?.();
    } catch (error) {
      console.error('Error unlinking supplier:', error);
      showError('Fehler beim Entverknüpfen des Lieferanten');
    } finally {
      setLoading(false);
      // Close the confirmation modal
      setDeleteConfirmModal({
        isOpen: false,
        supplierId: null,
        supplierName: ''
      });
    }
  };

  const handleUnlinkClick = (supplierId: string, supplierName: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      supplierId,
      supplierName
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmModal.supplierId) {
      unlinkSupplierFromGroup(deleteConfirmModal.supplierId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({
      isOpen: false,
      supplierId: null,
      supplierName: ''
    });
  };

  const loadSuppliersWithGLNs = async () => {
    setLoading(true);
    try {
      const response = await getSuppliersWithGLNs();
      const transformedSuppliers = Array.isArray(response) 
        ? response
            .map((supplier: SupplierGLNData): HierarchicalSupplier => ({
              ...supplier,
              totalArticles: supplier.articleCount || 0,
              glnNumbers: supplier.allGLNs?.map(gln => ({
                glnNumber: gln,
                companyName: supplier.name
              })) || []
            }))
            // Show all suppliers (both with and without linked suppliers)
            // This allows users to create new linkings and manage existing ones
        : [];
      setSuppliers(transformedSuppliers);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      showError('Fehler beim Laden der Lieferanten');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const searchSuppliers = async (term: string) => {
    if (!term.trim()) {
      loadInitialSuppliers();
      return;
    }

    console.log('🔍 Searching suppliers for term:', term);
    setSearchLoading(true);
    try {
      const response = await getSuppliers({
        page: 1,
        limit: 50,
        search: term,
        isActive: 'true'
      });
      
      console.log('📦 Search response:', response);
      
      // Handle different response formats
      let suppliersData;
      if (response && response.data) {
        // If response has a data property
        if (Array.isArray(response.data)) {
          suppliersData = response.data;
        } else if (response.data.suppliers && Array.isArray(response.data.suppliers)) {
          suppliersData = response.data.suppliers;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          suppliersData = response.data.data;
        } else {
          suppliersData = [];
        }
      } else if (Array.isArray(response)) {
        // If response is directly an array
        suppliersData = response;
      } else {
        suppliersData = [];
      }
      
      const allSuppliers = Array.isArray(suppliersData) ? suppliersData : [];
      
      console.log('📋 Search results found:', allSuppliers.length);
      
      // Get all already linked suppliers from existing hierarchical structure
      const linkedSupplierIds = new Set<string>();
      
      // Add suppliers from drop zone (both main suppliers and linked ones in drop zone)
      dropZoneSuppliers.forEach(group => {
        linkedSupplierIds.add(group._id); // Add main supplier from drop zone
        group.linkedSuppliers.forEach(linked => linkedSupplierIds.add(linked._id));
      });
      
      // Add suppliers that are already linked in the database (but NOT the main suppliers)
      try {
        const existingLinkedResponse = await getSuppliersWithGLNs();
        if (Array.isArray(existingLinkedResponse)) {
          existingLinkedResponse.forEach((supplier: HierarchicalSupplier) => {
            if (supplier.linkedSuppliers && supplier.linkedSuppliers.length > 0) {
              // DON'T add the main supplier (supplier._id) - only the linked ones
              supplier.linkedSuppliers.forEach(linked => {
                if (linked._id) linkedSupplierIds.add(linked._id);
              });
            }
          });
        }
      } catch (error) {
        console.warn('Warning: Could not load existing linked suppliers for filtering');
      }
      
      const filteredResults = allSuppliers.filter(
        (supplier: Supplier) => !linkedSupplierIds.has(supplier._id)
      );
      
      console.log('✅ Filtered search results:', filteredResults.length);
      console.log('🚫 Excluded linked suppliers:', linkedSupplierIds.size);
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      showError('Fehler bei der Lieferantensuche');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDragStart = (supplier: Supplier) => {
    setDraggedSupplier(supplier);
  };

  const handleDragEnd = () => {
    setDraggedSupplier(null);
  };

  const handleDropOnNewGroup = async (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedSupplier) return;

    // Create new group with dragged supplier as main
    const newGroup: DropZoneSupplier = {
      _id: draggedSupplier._id,
      name: draggedSupplier.name,
      kreditorNumber: draggedSupplier.kreditorNumber,
      isMainSupplier: true,
      linkedSuppliers: []
    };

    setDropZoneSuppliers(prev => [...prev, newGroup]);
    
    // Remove from search results
    setSearchResults(prev => prev.filter(s => s._id !== draggedSupplier._id));
    setDraggedSupplier(null);
  };

  const handleDropOnGroup = async (e: React.DragEvent, targetGroupIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedSupplier) return;

    const newDropZoneSuppliers = [...dropZoneSuppliers];
    const targetGroup = newDropZoneSuppliers[targetGroupIndex];

    // Add as linked supplier
    const newLinkedSupplier: DropZoneSupplier = {
      _id: draggedSupplier._id,
      name: draggedSupplier.name,
      kreditorNumber: draggedSupplier.kreditorNumber,
      isMainSupplier: false,
      linkedSuppliers: []
    };

    targetGroup.linkedSuppliers.push(newLinkedSupplier);
    setDropZoneSuppliers(newDropZoneSuppliers);
    
    // Remove from search results
    setSearchResults(prev => prev.filter(s => s._id !== draggedSupplier._id));
    setDraggedSupplier(null);
  };

  const removeSupplierFromGroup = (groupIndex: number, supplierIndex?: number) => {
    const newDropZoneSuppliers = [...dropZoneSuppliers];
    
    if (supplierIndex === undefined) {
      // Remove entire group
      newDropZoneSuppliers.splice(groupIndex, 1);
    } else {
      // Remove linked supplier
      newDropZoneSuppliers[groupIndex].linkedSuppliers.splice(supplierIndex, 1);
    }
    
    setDropZoneSuppliers(newDropZoneSuppliers);
  };

  const handleSaveOrganization = async () => {
    if (dropZoneSuppliers.length === 0) {
      showInfo('Keine Verknüpfungen zum Speichern vorhanden');
      return;
    }

    setSaving(true);
    try {
      for (const group of dropZoneSuppliers) {
        for (const linkedSupplier of group.linkedSuppliers) {
          await linkSuppliers(group._id, linkedSupplier._id);
        }
      }
      
      showSuccess('Verknüpfungen erfolgreich gespeichert');
      setDropZoneSuppliers([]);
      // Suche nicht automatisch neu laden nach dem Speichern
      
      if (onLinkingComplete) {
        onLinkingComplete();
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      showError('Fehler beim Speichern der Verknüpfungen');
    } finally {
      setSaving(false);
    }
  };

  const toggleSupplierExpansion = (supplierId: string) => {
    const newExpanded = new Set(expandedSuppliers);
    if (newExpanded.has(supplierId)) {
      newExpanded.delete(supplierId);
    } else {
      newExpanded.add(supplierId);
    }
    setExpandedSuppliers(newExpanded);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Lieferanten-Verknüpfung verwalten
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Organisieren Sie Lieferanten-Hierarchien und verwalten Sie GLN-Verknüpfungen
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('organize')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'organize'
                    ? 'border-[#006341] text-[#006341]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DragIcon className="w-4 h-4 inline mr-2" />
                Lieferanten organisieren
              </button>
              <button
                onClick={() => setActiveTab('view')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'view'
                    ? 'border-[#006341] text-[#006341]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <LinkIcon className="w-4 h-4 inline mr-2" />
                Bestehende Verknüpfungen
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="flex h-[calc(90vh-180px)]">
            {activeTab === 'organize' && (
              <>
                {/* Left: Search Panel */}
                <div className="w-1/2 border-r border-gray-200 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Lieferanten suchen</h4>
                      <button
                        onClick={() => {
                          console.log('🔄 Manual reload triggered');
                          loadInitialSuppliers();
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 border border-gray-300 rounded"
                        title="Neuladen"
                      >
                        ↻ Reload
                      </button>
                    </div>
                    <div className="relative">
                      <SearchIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Name, Kreditor oder E-Mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#006341] focus:border-[#006341] text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3">
                    {searchLoading && (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#006341]"></div>
                      </div>
                    )}
                    
                    <div className="space-y-1">
                      {searchResults.map((supplier) => (
                        <div
                          key={supplier._id}
                          draggable
                          onDragStart={() => handleDragStart(supplier)}
                          onDragEnd={handleDragEnd}
                          className="bg-white border border-gray-200 rounded p-2 cursor-move hover:border-[#006341] hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center space-x-2">
                            <DragIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm truncate">{supplier.name}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {supplier.kreditorNumber && `K: ${supplier.kreditorNumber} • `}
                                {supplier.email}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {searchTerm && !searchLoading && searchResults.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <SearchIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Keine Lieferanten gefunden</p>
                      </div>
                    )}

                    {!searchTerm && !searchLoading && searchResults.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <SearchIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Lade Lieferanten...</p>
                        <p className="text-sm">Durchsucht werden ca. 1400 Lieferanten</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Drop Zone */}
                <div className="w-1/2 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-green-50">
                    <h4 className="font-medium text-green-900 mb-1 text-sm">Verknüpfungsbereich</h4>
                    <p className="text-xs text-green-800">
                      Ziehen Sie Lieferanten hierher. Der erste wird zum Hauptlieferanten.
                    </p>
                  </div>

                  <div 
                    className="flex-1 overflow-y-auto p-3"
                    onDrop={handleDropOnNewGroup}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <div className="space-y-2">
                      {dropZoneSuppliers.map((group, groupIndex) => (
                        <div
                          key={`group-${group._id}-${groupIndex}`}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                          onDrop={(e) => handleDropOnGroup(e, groupIndex)}
                          onDragOver={(e) => e.preventDefault()}
                        >
                          {/* Main Supplier */}
                          <div className="p-2 bg-[#006341] text-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0"></div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{group.name}</div>
                                  <div className="text-xs opacity-90 truncate">
                                    {group.kreditorNumber && `K: ${group.kreditorNumber} • `}
                                    Hauptlieferant
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs bg-white bg-opacity-20 px-1.5 py-0.5 rounded">
                                  {group.linkedSuppliers.length}
                                </span>
                                <button
                                  onClick={() => removeSupplierFromGroup(groupIndex)}
                                  className="p-1 text-white hover:text-red-300 transition-colors"
                                  title="Gruppe entfernen"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Linked Suppliers */}
                          {group.linkedSuppliers.length > 0 && (
                            <div className="p-2 space-y-1 bg-gray-50">
                              {group.linkedSuppliers.map((linkedSupplier, supplierIndex) => (
                                <div
                                  key={`linked-${linkedSupplier._id}-${supplierIndex}`}
                                  className="flex items-center justify-between p-1.5 bg-white border border-gray-200 rounded"
                                >
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <div className="w-1 h-1 bg-gray-400 rounded-full ml-1 flex-shrink-0"></div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-medium text-gray-900 truncate">{linkedSupplier.name}</div>
                                      {linkedSupplier.kreditorNumber && (
                                        <div className="text-xs text-gray-500 truncate">
                                          K: {linkedSupplier.kreditorNumber}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => removeSupplierFromGroup(groupIndex, supplierIndex)}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Aus Gruppe entfernen"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {dropZoneSuppliers.length === 0 && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                          <DragIcon className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                          <h5 className="text-sm font-medium text-gray-900 mb-1">
                            Lieferanten hierher ziehen
                          </h5>
                          <p className="text-xs text-gray-500">
                            Ziehen Sie Lieferanten hierher, um Verknüpfungen zu erstellen.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  {dropZoneSuppliers.length > 0 && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <button
                        onClick={handleSaveOrganization}
                        disabled={saving}
                        className="w-full px-3 py-2 bg-[#006341] text-white rounded hover:bg-[#004d30] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm font-medium"
                      >
                        {saving && (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        )}
                        <span>
                          {saving ? 'Speichere...' : 'Verknüpfungen speichern'}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'view' && (
              <div className="w-full flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center gap-4">
                  <div className="relative flex-1">
                    <SearchIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Haupt- oder Unterkonto suchen: Name, Kreditor, E-Mail, GLN..."
                      value={viewSearch}
                      onChange={(e) => setViewSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#006341] focus:border-[#006341] text-sm"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={onlyWithLinks}
                      onChange={(e) => setOnlyWithLinks(e.target.checked)}
                      className="rounded border-gray-300 text-[#006341] focus:ring-[#006341]"
                    />
                    Nur mit Verknüpfungen
                  </label>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {visibleSuppliers.length} von {suppliers.length}
                  </span>
                </div>
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006341]"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleSuppliers.map((supplier, index) => (
                      <div
                        key={supplier._id || `fallback-${index}`}
                        className={`border rounded-lg ${supplier.isOrphanedLink ? 'border-red-200' : 'border-gray-200'}`}
                      >
                        <div
                          className="p-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                          onClick={() => supplier._id && toggleSupplierExpansion(supplier._id)}
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {supplier._id && isGroupExpanded(supplier) ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 text-sm truncate">{supplier.name}</h4>
                              <div className="flex items-center space-x-2 text-xs text-gray-500">
                                {supplier.kreditorNumber && (
                                  <span>Kreditor: {supplier.kreditorNumber}</span>
                                )}
                                {supplier.isPrimary && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    Haupt
                                  </span>
                                )}
                                {supplier.isOrphanedLink && (
                                  <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                                    Verknüpft, Hauptkonto fehlt/inaktiv
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            {supplier.isOrphanedLink && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlinkClick(supplier._id, supplier.name);
                                }}
                                className="px-2 py-1 text-red-700 bg-red-50 hover:bg-red-100 rounded"
                                title="Verknüpfung entfernen"
                              >
                                Entverknüpfen
                              </button>
                            )}
                            <span>{(supplier.totalArticles || supplier.articleCount || 0)} Art.</span>
                            {supplier.allGLNs && supplier.allGLNs.length > 0 && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                {supplier.allGLNs.length} GLN
                              </span>
                            )}
                            {supplier.linkedSuppliers && supplier.linkedSuppliers.length > 0 && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {supplier.linkedSuppliers.length} verknüpft
                              </span>
                            )}
                          </div>
                        </div>

                        <AnimatePresence>
                          {supplier._id && isGroupExpanded(supplier) && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-gray-200 overflow-hidden"
                            >
                              <div className="p-4 bg-gray-50 space-y-4">
                               

                                {/* Linked Suppliers */}
                                {supplier.linkedSuppliers && supplier.linkedSuppliers.length > 0 && (
                                  <div>
                                    <h5 className="font-medium text-gray-900 mb-2 text-sm">Verknüpfte Lieferanten:</h5>
                                    <div className="space-y-1">
                                      {supplier.linkedSuppliers.map((linkedSupplier, linkedIndex) => (
                                        <div 
                                          key={`${supplier._id}-linked-${linkedSupplier._id || linkedIndex}`}
                                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded"
                                        >
                                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                                            <span className={`font-medium text-sm truncate ${viewTerm && matchesSupplier(linkedSupplier, viewTerm) ? 'text-[#006341] underline' : 'text-gray-900'}`}>{linkedSupplier.name}</span>
                                            {linkedSupplier.kreditorNumber && (
                                              <span className="text-xs text-gray-500 truncate">
                                                (K: {linkedSupplier.kreditorNumber})
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-500">
                                              {linkedSupplier.articleCount} Art.
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                linkedSupplier._id && handleUnlinkClick(linkedSupplier._id, linkedSupplier.name);
                                              }}
                                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                              title="Verknüpfung entfernen"
                                            >
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}

                    {visibleSuppliers.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <LinkIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p>Keine Lieferanten mit Verknüpfungen gefunden</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Verknüpfung entfernen"
          message={`Möchten Sie "${deleteConfirmModal.supplierName}" wirklich entverknüpfen?`}
          confirmText="Entverknüpfen"
          loading={loading}
        />
      </div>
    </AnimatePresence>
  );
};